import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import { User } from '../models/user.model';
import { UserRole } from '../enums/user-role.enum';
import { normalizeApiArray } from '../utils/api-response.util';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${environment.apiUrl}/User`;

  constructor(private http: HttpClient) { }

  // Get all users
  getUsers(organisationId?: number): Observable<User[]> {
    const url = organisationId 
      ? `${this.apiUrl}/organisation/${organisationId}`
      : this.apiUrl;
    return this.http.get<unknown>(url).pipe(
      map(response => normalizeApiArray<User>(response))
    );
  }

  // Get user by ID
  getUserById(id: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

  // Create new user
  createUser(userData: Partial<User>): Observable<User> {
    return this.http.post<User>(this.apiUrl, userData);
  }

  // Update user
  updateUser(id: number, userData: Partial<User>): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${id}`, userData);
  }

  // Delete user
  deleteUser(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  // Update user role
  updateUserRole(id: number, role: UserRole): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${id}/role`, { role });
  }

  // Update user password
  updatePassword(id: number, oldPassword: string, newPassword: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}/password`, { oldPassword, newPassword });
  }

  // Get drivers (users with driver role or permission)
  getDrivers(organisationId?: number): Observable<User[]> {
    const url = organisationId 
      ? `${this.apiUrl}/drivers/organisation/${organisationId}`
      : `${this.apiUrl}/drivers`;
    return this.http.get<User[]>(url);
  }

  // Get operators
  getOperators(): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/operators`);
  }

  // Get passengers
  getPassengers(organisationId?: number): Observable<User[]> {
    const url = organisationId 
      ? `${this.apiUrl}/passengers/organisation/${organisationId}`
      : `${this.apiUrl}/passengers`;
    return this.http.get<User[]>(url);
  }

  // Get user statistics
  getUserStats(): Observable<{
    totalUsers: number;
    admins: number;
    operators: number;
    drivers: number;
    passengers: number;
    activeUsers: number;
    inactiveUsers: number;
  }> {
    return this.http.get<any>(`${this.apiUrl}/stats`);
  }

  // Search users
  searchUsers(query: string): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/search`, {
      params: { query }
    });
  }

  // Reset user password (admin only)
  resetPassword(id: number): Observable<{ temporaryPassword: string }> {
    return this.http.post<{ temporaryPassword: string }>(
      `${this.apiUrl}/${id}/reset-password`,
      {}
    );
  }

  // Enable/disable user
  toggleUserStatus(id: number, isActive: boolean): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${id}/status`, { isActive });
  }

  // Get user activity log
  getUserActivity(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${id}/activity`);
  }

  // Update user profile
  updateProfile(id: number, profileData: {
    userName?: string;
    userEmail?: string;
    phoneNumber?: string;
    address?: string;
  }): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${id}/profile`, profileData);
  }

  // Upload profile picture
  uploadProfilePicture(id: number, file: File): Observable<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.http.post<{ imageUrl: string }>(
      `${this.apiUrl}/${id}/profile-picture`,
      formData
    );
  }

  // Verify user email
  verifyEmail(token: string): Observable<{ verified: boolean }> {
    return this.http.post<{ verified: boolean }>(
      `${this.apiUrl}/verify-email`,
      { token }
    );
  }

  // Send password reset email
  forgotPassword(email: string): Observable<{ sent: boolean }> {
    return this.http.post<{ sent: boolean }>(
      `${this.apiUrl}/forgot-password`,
      { email }
    );
  }

  // Reset password with token
  resetPasswordWithToken(token: string, newPassword: string): Observable<{ reset: boolean }> {
    return this.http.post<{ reset: boolean }>(
      `${this.apiUrl}/reset-password`,
      { token, newPassword }
    );
  }
}
